import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SplashCinematic = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 600),   // Books
      setTimeout(() => setStage(2), 2000),  // AI  
      setTimeout(() => setStage(3), 3400),  // People
      setTimeout(() => setStage(4), 4800),  // Taraanaya
      setTimeout(() => navigate('/onboarding-new'), 7500)
    ];

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ background: '#0A0E17' }}
    >
      {/* Ambient background glow */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(34, 211, 238, 0.06) 0%, transparent 60%)'
        }}
      />

      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {/* Stage 1: Books - Simplified & BRIGHT */}
          {stage >= 1 && stage < 2 && (
            <motion.div
              key="books"
              className="flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.7 }}
            >
              <motion.div className="text-6xl" animate={{ y: [-5, 5, -5] }} transition={{ duration: 2, repeat: Infinity }}>
                📚
              </motion.div>
            </motion.div>
          )}

          {/* Stage 2: AI - Simplified & BRIGHT */}
          {stage >= 2 && stage < 3 && (
            <motion.div
              key="ai"
              className="flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.7 }}
            >
              <motion.div className="text-6xl" animate={{ rotate: [0, 360] }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}>
                🤖
              </motion.div>
            </motion.div>
          )}

          {/* Stage 3: People - Simplified & BRIGHT */}
          {stage >= 3 && stage < 4 && (
            <motion.div
              key="people"
              className="flex items-center justify-center gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 1.2 }}
              transition={{ duration: 0.7 }}
            >
              {['👤', '👥', '👤'].map((emoji, i) => (
                <motion.div
                  key={i}
                  className="text-5xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                >
                  {emoji}
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Stage 4: TARAANAYA - EXTREME BRIGHTNESS */}
          {stage >= 4 && (
            <motion.div
              key="logo"
              className="flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {/* MASSIVE Background Glow */}
              <motion.div
                className="absolute"
                style={{
                  width: '1000px',
                  height: '1000px',
                  background: 'radial-gradient(circle, rgba(34, 211, 238, 0.2) 0%, rgba(99, 102, 241, 0.12) 35%, rgba(139, 92, 246, 0.08) 60%, transparent 80%)',
                  filter: 'blur(100px)',
                }}
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1.2 }}
                transition={{ duration: 2 }}
              />

              {/* 5 Stars in Arc - BRIGHT & CLEAR */}
              <motion.div
                className="mb-12 relative z-10"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
              >
                <svg width="300" height="110" viewBox="0 0 300 110">
                  <defs>
                    <linearGradient id="starsGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#22D3EE" />
                      <stop offset="50%" stopColor="#818CF8" />
                      <stop offset="100%" stopColor="#C084FC" />
                    </linearGradient>
                    <filter id="starGlow" x="-100%" y="-100%" width="300%" height="300%">
                      <feGaussianBlur stdDeviation="8" />
                    </filter>
                  </defs>
                  
                  {/* Arc line */}
                  <motion.path
                    d="M 50 85 Q 150 30, 250 85"
                    stroke="url(#starsGrad)"
                    strokeWidth="3"
                    fill="none"
                    opacity="0.5"
                    filter="url(#starGlow)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.2, delay: 0.5 }}
                  />
                  
                  {/* 5 Four-pointed stars */}
                  {[
                    { x: 50, y: 85, size: 16 },
                    { x: 100, y: 55, size: 18 },
                    { x: 150, y: 35, size: 22 },
                    { x: 200, y: 55, size: 18 },
                    { x: 250, y: 85, size: 16 },
                  ].map((star, i) => (
                    <motion.g
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.7 + i * 0.1 }}
                    >
                      {/* White core */}
                      <circle cx={star.x} cy={star.y} r={star.size * 0.25} fill="#FFFFFF" opacity="0.9" />
                      {/* 4-pointed star */}
                      <path
                        d={`
                          M ${star.x} ${star.y - star.size}
                          L ${star.x + star.size * 0.4} ${star.y - star.size * 0.4}
                          L ${star.x + star.size} ${star.y}
                          L ${star.x + star.size * 0.4} ${star.y + star.size * 0.4}
                          L ${star.x} ${star.y + star.size}
                          L ${star.x - star.size * 0.4} ${star.y + star.size * 0.4}
                          L ${star.x - star.size} ${star.y}
                          L ${star.x - star.size * 0.4} ${star.y - star.size * 0.4}
                          Z
                        `}
                        fill="url(#starsGrad)"
                        filter="url(#starGlow)"
                      />
                    </motion.g>
                  ))}
                </svg>
              </motion.div>

              {/* "taraanaya" text - ULTRA BRIGHT */}
              <motion.div
                className="relative z-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 1.3 }}
              >
                {/* Cyan glow layer */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    fontSize: '110px',
                    fontWeight: 400,
                    color: '#22D3EE',
                    letterSpacing: '5px',
                    fontStyle: 'italic',
                    filter: 'blur(50px)',
                    opacity: 0.8,
                  }}
                >
                  taraanaya
                </div>
                {/* White glow layer */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    fontSize: '110px',
                    fontWeight: 400,
                    color: '#FFFFFF',
                    letterSpacing: '5px',
                    fontStyle: 'italic',
                    filter: 'blur(20px)',
                    opacity: 0.6,
                  }}
                >
                  taraanaya
                </div>
                {/* Main text with gradient */}
                <h1
                  style={{
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    fontSize: '110px',
                    fontWeight: 400,
                    background: 'linear-gradient(90deg, #22D3EE 0%, #60A5FA 20%, #818CF8 50%, #A78BFA 80%, #C084FC 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '5px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    filter: 'drop-shadow(0 0 50px rgba(34, 211, 238, 0.9)) drop-shadow(0 0 100px rgba(139, 92, 246, 0.7))',
                    position: 'relative',
                  }}
                >
                  taraanaya
                </h1>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SplashCinematic;
